// combatpro/panels/spells/SpellInfo.js
//
// Combat PRO - SpellInfo (parity restoration of the spell detail card).
//
// The modular migration left this as an empty mount, which dropped the
// established spell detail card entirely (the composition the good-state
// screenshots show: school / level / concentration tags, range, description and
// the Range / Concentration meta columns, wrapped in the school-coloured border).
//
// This restores that card using ONLY established data + logic:
//   - the spell record is resolved via getSpellData against the same injected
//     window.VI_SPELLS_DB that SpellExec / SpellSlot already read (no new DB);
//   - the border + translucent background come from the established school colour
//     table getSchoolColor / SPELL_COLORS (evocation red ... enchantment purple,
//     illusion pink, abjuration green) - the authoritative colour identity, not
//     an invented palette;
//   - all fields (school, level, range, concentration, desc) are read straight
//     off the processed record; nothing is recomputed or fabricated.
//
// Read-only render. No handlers, no state writes, no DOM manipulation, no
// optional chaining, no em dash literal. When no spell is selected (or the record
// is not found) it renders the original empty mount, so an empty slot is
// byte-identical to before.

import React, { useContext } from 'react';
import { CombatContext } from '../../contexts/CombatContext';
import { getSpellData, getSchoolColor } from '../../spell/spellEngine';

function SpellInfo(props) {
  const index = props.index;
  const spellIdx = props.spellIdx;
  const { state } = useContext(CombatContext);
  const mountId = 'spellInfo' + spellIdx + '-' + index;

  const monster = state.monsters[index];
  const slot = (monster && monster.spells) ? monster.spells[spellIdx] : null;
  const spellName = (slot && slot.name) ? slot.name : '';
  if (!spellName) { return <div className="spell-info" id={mountId}></div>; }

  const spellsDb = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];
  const spell = getSpellData(spellName, spellsDb);
  if (!spell) { return <div className="spell-info" id={mountId}></div>; }

  const school = spell.school || '';
  const color = getSchoolColor(school);
  const schoolLabel = school ? (school.charAt(0).toUpperCase() + school.slice(1)) : '';

  const level = (spell.level !== undefined && spell.level !== null) ? spell.level : null;
  const levelLabel = (level === 0) ? 'Cantrip' : (level !== null ? ('Level ' + level) : '');

  const rangeText = spell.range || '';
  const conc = !!spell.concentration;
  const descText = spell.desc || '';

  return (
    <div className="spell-info spell-info-filled" id={mountId} style={{ borderColor: color.border, background: color.bg }}>
      <div className="vi-spell-info-tags">
        {schoolLabel ? <span className="vi-spell-info-tag" style={{ borderColor: color.border, color: color.border }}>📖 {schoolLabel}</span> : null}
        {levelLabel ? <span className="vi-spell-info-tag vi-spell-info-tag-level">{levelLabel}</span> : null}
        {conc ? <span className="vi-spell-info-tag vi-spell-info-tag-conc">⚠️ Conc</span> : null}
      </div>
      {rangeText ? <div className="vi-spell-info-range">🎯 {rangeText}</div> : null}
      {descText ? <div className="vi-spell-info-desc">{descText}</div> : null}
      <div className="vi-spell-info-meta">
        {rangeText ? (
          <div className="vi-spell-info-metacol">
            <div className="vi-spell-info-metalabel">Range</div>
            <div className="vi-spell-info-metaval">{rangeText}</div>
          </div>
        ) : null}
        {conc ? (
          <div className="vi-spell-info-metacol">
            <div className="vi-spell-info-metalabel">Concentration</div>
            <div className="vi-spell-info-metaval">Required</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default SpellInfo;
